import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { escapeWouldClearField } from './clearOnEscape';
import { useCloseOnBack } from './backLayer/useCloseOnBack';
import './SchemaModal.css';

/**
 * Baustein 5 — Modal-Vokabular.
 * Einheitlicher Stil für alle Picker, Confirm-Dialoge, Forms, Detail-Modale.
 *
 * Props:
 *   open, onOpenChange — Radix Dialog Steuerung
 *   size               — 'sm' | 'md' | 'lg' | 'xl' (default 'md'). xl füllt den Bildschirm bis auf
 *                        einen 24-px-Rand ringsum, Breite und Höhe (die anderen höchstens 85vh hoch) —
 *                        für Inhalte mit fester Bildspalte und scrollenden Abschnitten (CardDetailModal).
 *   title              — Header-Titel
 *   description        — optional kurzer Subtitel unter dem Titel (auch für a11y).
 *                        Wenn nicht gesetzt, opt-out vom Radix-Description-Requirement.
 *   children           — Body-Inhalt
 *   footer             — Footer-Slot (Primary rechts, Sekundär links davon)
 *   dismissOnBackdrop  — boolean, ob Klick auf Backdrop schließt (default true).
 *                        Bei Forms mit Eingabe auf false setzen.
 *
 * Unter 480px Viewport-Breite bekommt das Modal einen festen Seitenrand
 * (`max-width: calc(100vw - 2 * var(--space-sm))`) statt Kante-an-Kante zu
 * kleben — betrifft alle Konsumenten (`ExportModal`, `AddCategoryDialog`,
 * `NewLabelModal`, `ConfirmDialog`).
 *
 * Am Handy (≤900px) schließt auch die Zurück-Geste — auch bei
 * `dismissOnBackdrop={false}`: ohne diese Anbindung würde ein Zurück-Druck dort die
 * ganze Seite verlassen, die Eingabe wäre damit ebenso weg wie beim abgeschalteten
 * Backdrop-Klick, plus der Kontext der Seite selbst.
 */
export default function SchemaModal({
  open,
  onOpenChange,
  size = 'md',
  title,
  description,
  children,
  footer,
  dismissOnBackdrop = true,
}) {
  useCloseOnBack(open, () => onOpenChange?.(false));

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="schema-modal__backdrop" />
        <Dialog.Content
          className={`schema-modal schema-modal--${size}`}
          onInteractOutside={(e) => {
            if (!dismissOnBackdrop) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (escapeWouldClearField()) e.preventDefault();
          }}
        >
          <div className="schema-modal__header">
            <div className="schema-modal__titles">
              <Dialog.Title className="schema-modal__title">{title}</Dialog.Title>
              <Dialog.Description
                className={
                  description
                    ? 'schema-modal__description'
                    : 'schema-modal__description schema-modal__description--sr-only'
                }
              >
                {description || title}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="schema-modal__close" aria-label="Schließen">
                <X size={18} aria-hidden />
              </button>
            </Dialog.Close>
          </div>
          <div className="schema-modal__body">{children}</div>
          {footer && <div className="schema-modal__footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
